import React from "react"
import PropTypes from "prop-types"
import usePresence from "../hooks/usePresence"

/**
 * Reusable online/offline indicator.
 *
 * @example
 * <OnlineStatus userId={user.id} />
 * <OnlineStatus userId={user.id} variant="dot" />
 */
const OnlineStatus = ({
  userId,
  variant = "label",
  className = "",
  onlineLabel = "Online",
  offlineLabel = "Offline",
}) => {
  const { isOnline, connectionState } = usePresence()
  const online = isOnline(userId)
  const connectionReady =
    connectionState === "connected" || connectionState === "connecting"

  // When Pusher itself is down, avoid flashing everyone as Offline.
  // Prefer last known presence; if never known, show Offline muted.
  const statusClass = online ? "online" : "offline"
  const showUnknown = !connectionReady && !online

  if (variant === "dot") {
    const classes = className
      ? `${className} ${className}--${statusClass}${
          showUnknown ? ` ${className}--unknown` : ""
        }`
      : `online-status online-status--dot online-status--${statusClass}${
          showUnknown ? " online-status--unknown" : ""
        }`

    return (
      <span
        className={classes.trim()}
        title={online ? onlineLabel : offlineLabel}
        aria-label={online ? onlineLabel : offlineLabel}
        data-connection={connectionState}
      />
    )
  }

  return (
    <span
      className={`online-status online-status--label online-status--${statusClass}${
        showUnknown ? " online-status--unknown" : ""
      } ${className}`.trim()}
      data-connection={connectionState}
    >
      <span className="online-status__icon" aria-hidden="true">
        {online ? "🟢" : "⚪"}
      </span>
      <span className="online-status__text">
        {online ? onlineLabel : offlineLabel}
      </span>
    </span>
  )
}

OnlineStatus.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  variant: PropTypes.oneOf(["label", "dot"]),
  className: PropTypes.string,
  onlineLabel: PropTypes.string,
  offlineLabel: PropTypes.string,
}

export default OnlineStatus
